/**
 * 路由守卫模块
 * 将路由守卫逻辑拆分为独立的模块，提高可维护性
 */

import { isAccessAllowed } from '@/utils/wechatEnvCheck.js'
import { isLoggedIn, isTokenValid } from '@/utils/auth.js'
import { isDevModeEnabled } from '@/utils/devMode.js'
import { ORDER_PENDING_DETAIL_KEY } from '@/utils/orderFee.js'

/**
 * 微信环境检测守卫
 * 在生产环境下检查是否在微信中访问
 */
export function wechatEnvGuard(to, from, next) {
  // 跳过微信提示页面本身
  if (to.path === '/wechat-required') {
    return next()
  }

  // 价格电视大屏页不做微信环境限制，直接放行
  if (to.path === '/price/tv' || to.path.startsWith('/price/tv/')) {
    return next()
  }

  // 仅在生产环境检测
  if (process.env.NODE_ENV !== 'production') {
    return next()
  }

  // 检查是否需要微信环境
  if (shouldCheckWechatEnv(to)) {
    if (!isAccessAllowed()) {
      return next('/wechat-required')
    }
  }

  next()
}

/**
 * 判断路由是否需要检测微信环境
 * @param {Object} to - 目标路由
 * @returns {boolean}
 */
function shouldCheckWechatEnv(to) {
  const path = to.path || ''

  // /price/tv 页面不需要微信环境检查（含子路径、尾斜杠）
  if (path === '/price/tv' || path.startsWith('/price/tv/')) return false

  // 仅限制：wx_views 下的页面 + price/index、price/member 页面
  if (path === '/price/index' || path === '/price/member') return true

  // 检查组件路径
  if (to.matched && to.matched.length > 0) {
    for (const route of to.matched) {
      if (route.component && typeof route.component === 'function') {
        const componentStr = route.component.toString()
        // 明确排除电视价格页面组件
        if (componentStr.includes('price/tv-price.vue')) return false
        // wx_views 或 price/index 需要微信限制
        if (componentStr.includes('wx_views') || componentStr.includes('price/index.vue') || componentStr.includes('price/member-index.vue')) return true
      }
    }
  }

  return false
}

/**
 * 认证守卫
 * 检查用户是否已登录
 */
export function authGuard(to, from, next) {
  // 开发模式跳过认证检查
  if (isDevModeEnabled()) {
    return next()
  }

  // 公开价格页面不需要 token；会员盘价需登录
  if (to.path === '/price/index' || to.path === '/price/tv') {
    return next()
  }

  // 检查是否需要认证
  const requiresAuth = to.matched.some(record => record.meta?.requiresAuth)

  // 静态PDF预览允许未登录访问
  const isStaticPdfPreview = 
    (to.path === '/pdf-preview' || to.path === 'pdf-preview') &&
    (to.query?.static === '1' || to.query?.static === 'true')

  if (isStaticPdfPreview) {
    return next()
  }

  // 不需要认证的页面直接放行
  if (!requiresAuth) {
    return next()
  }

  // 检查登录状态
  const loggedIn = isLoggedIn()

  // 未登录时清理无效token
  if (!loggedIn) {
    const hasToken = !!localStorage.getItem('auth_token')
    if (hasToken && !isTokenValid()) {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_id')
    }
  }

  // 处理登录页面
  if (to.path === '/login' || to.path.startsWith('/login/')) {
    if (loggedIn) {
      // 避免循环跳转，检查是否已经在首页或正在跳转到首页
      if (from.path === '/index/home' || from.path === '/index' || from.path === '/' || from.path === '/login') {
        return next()
      }
      // 已登录访问登录页，跳转到首页
      const redirect = to.query.redirect
      if (redirect && redirect !== '/login' && !redirect.startsWith('/login/')) {
        return next(redirect)
      }
      return next('/index/home')
    }
    return next()
  }

  // 处理后台登录页面
  if (to.path === '/admin/login') {
    if (loggedIn) {
      return next('/admin')
    }
    return next()
  }

  // 需要认证但未登录
  if (requiresAuth && !loggedIn) {
    // 根据路径判断跳转到哪个登录页
    if (to.path.startsWith('/admin')) {
      return next({ path: '/admin/login', query: { redirect: to.fullPath } })
    } else {
      return next({ path: '/login', query: { redirect: to.fullPath } })
    }
  }

  next()
}

/**
 * 设置所有路由守卫
 * @param {Object} router - Vue Router实例
 */
export function setupRouterGuards(router) {
  // 注册守卫（按顺序执行）
  router.beforeEach(wechatEnvGuard)
  router.beforeEach(authGuard)

  // 全局后置钩子
  router.afterEach((to) => {
    document.title = to.meta?.title || '渤格循环科技'
    window.scrollTo(0, 0)

    // 下单成功回退后：replace 到详情，避免历史栈回到下单页
    const pendingId = sessionStorage.getItem(ORDER_PENDING_DETAIL_KEY)
    if (!pendingId) return
    if (/\/my\/order\/detail/.test(to.path)) {
      sessionStorage.removeItem(ORDER_PENDING_DETAIL_KEY)
      return
    }
    sessionStorage.removeItem(ORDER_PENDING_DETAIL_KEY)
    router.replace({ path: `/my/order/detail/${pendingId}`, query: { status: 1, fromSubmit: '1' } })
  })
}

export default {
  setupRouterGuards,
  wechatEnvGuard,
  authGuard
}

